import { useRef, useState } from 'react';
import * as Sentry from '@sentry/react';
import { trackConversion } from '@/lib/analytics';
import type { OptimizeOpts } from '@/hooks/useOptimizationJob';

/**
 * Wie viele Produkte ein Lauf umfasst: das Minimum aus der freien Kapazitaet
 * und dem Bestand, den der Server auswaehlen WIRD.
 *
 * `remainingQuota` traegt seit dem Bestandsmass die freie KAPAZITAET
 * (`useEnforcement.remainingCapacity` aus `check_can_optimize.remaining_capacity`),
 * nicht mehr die Differenz aus Tarif-Limit und Lifetime-Verbrauch. Der
 * Parametername ist geblieben, weil die Rechnung dieselbe ist; die Bedeutung
 * hat sich gedreht. Wer hier wieder `limit - optimized_count` hereinreicht,
 * bekommt eine Zahl, die nach jedem geloeschten Produkt zu klein ist.
 *
 * Frueher eine harte Konstante 200 mit dem Kommentar "free-quota-friendly
 * batch size for a first-touch upsell" -- gedacht war sie fuer den
 * Erstkontakt, gegolten hat sie fuer alle. Bei einem Feed mit 4.800
 * unoptimierten Produkten bedeutete das 24 Runden aus optimieren, freigeben,
 * hochladen.
 *
 * `selectableCount` heisst bewusst nicht mehr `notOptimized`: die Menge, die
 * der Server nach `optimizationStatus: 'not_optimized'` findet, ist NICHT der
 * Bestand, den das Frontend vor dem Aufruf gemessen hat.
 * `enqueue-optimization-batches` setzt als ersten Schreibzugriff feed-weit
 * ALLE `failed`-Produkte auf `not_optimized` (index.ts, `Reset failed products
 * from previous runs`) und filtert erst danach. Der Aufrufer muss deshalb
 * `notOptimized + failed` hereinreichen — sonst beschriftet der Knopf 100 und
 * der Lauf umfasst 150, und die Kontingent-Vorwarnung in `handleOptimize`
 * rechnet mit der falschen Zahl. `user_optimization_usage` hat keinen
 * Decrement-Pfad, die 50 zusaetzlichen Slots sind endgueltig weg.
 *
 * Null heisst: gar nicht anbieten. Fuer erschoepfte Kapazitaet ist der
 * blocked-Zustand der JourneyChips zustaendig (`journey.ts`, Ziel `/pricing`),
 * nicht ein Knopf mit der Aufschrift "0 Produkte optimieren".
 *
 * Eine ueberzogene Kapazitaet ist ein erreichbarer Zustand — ein Konto kann
 * nach einem Tarifwechsel nach unten (oder durch eine Admin-Korrektur) mehr
 * Produkte betreuen, als sein Limit hergibt. Als NEGATIVE Zahl kommt sie hier
 * allerdings nicht mehr an: `check_can_optimize` klemmt `remaining_capacity`
 * serverseitig mit `GREATEST(0, …)`, und `useEnforcement` tut dasselbe in
 * seinem Rueckfall (`Math.max(0, limit - count)`). Das `Math.max(0, …)` unten
 * ist damit heute keine Korrektur mehr, sondern ein Riegel gegen einen
 * kuenftigen Aufrufer, der wieder selbst rechnet.
 */
export function resolveOptimizeTarget({
  remainingQuota,
  selectableCount,
}: { remainingQuota: number; selectableCount: number }): number {
  return Math.max(0, Math.min(remainingQuota, selectableCount));
}

/** Source of the click that triggered `runOptimize`. Vier Einstiege, eine
 * Aktion (G3: no competing CTAs) — der Marker speist ausschliesslich das
 * Analytics-Ereignis:
 *   `cta`    — die Hauptkarte am Ende des Reports (`FeedCheckUpsellCta`)
 *   `issue`  — ein "Das beheben lassen" an einer einzelnen Baustelle
 *   `inline` — der Knopf direkt unter der Verlust-Zeile im Kopf
 *   `sticky` — die eingeblendete Leiste am unteren Rand
 * Vorher meldeten drei dieser vier `cta`; jede Aussage darueber, welcher
 * Einstieg konvertiert, war damit falsch. */
export type FeedCheckOptimizeSource = 'cta' | 'issue' | 'inline' | 'sticky';

export interface UseFeedCheckOptimizeResult {
  /** Fordert einen Lauf ueber die `target` noch nicht optimierten Produkte des
   * Feeds an — per Server-Selection, ohne eigene Id-Liste. No-op solange
   * `feedId` null ist oder `target` nichts hergibt. */
  runOptimize: (source: FeedCheckOptimizeSource) => Promise<void>;
  loading: boolean;
}

/**
 * Single source of truth for the Feed-Check report's "optimize my unoptimized
 * products" action (G3). Extracted from `FeedCheckUpsellCta` so the header's
 * per-issue buttons and the report's primary CTA share one action, one
 * loading state and one analytics event — never two competing actions.
 *
 * `target` kommt von aussen, weil die Zahl auch auf den Knoepfen steht.
 * Berechnet wird sie einmal mit `resolveOptimizeTarget`; die Menge, die der
 * Knopf BESCHRIFTET, ist damit dieselbe, die die Aktion ANFORDERT.
 *
 * ────────────────────────────────────────────────────────────────────────────
 * WARUM HIER KEINE `product_scores`-ABFRAGE MEHR STEHT
 *
 * Bis 2026-08-02 holte dieser Hook selbst die schwaechsten Produkte:
 * `product_scores` nach `total_score` sortiert, ueber den Fremdschluessel auf
 * `products.optimization_status` gefiltert, das Ergebnis als Id-Liste an
 * `enqueue-optimization-batches`. Zwei Messungen auf dem Kundenfeed
 * `rd_chde_tyres` (`6011578a-f12d-4cb1-8b1f-b4a16946d747`, 2026-08-02) haben
 * das erledigt:
 *
 *  1. LAUFZEIT. `EXPLAIN ANALYZE` derselben Abfrage mit `LIMIT 200`:
 *     8.176 ms warm (13.007 ms kalt) gegen ein `statement_timeout` von 8 s fuer
 *     die Rolle `authenticated`. Der `LIMIT` kann nicht greifen, weil kaum
 *     etwas durch den Join kommt: 56.076 Score-Zeilen werden einzeln gegen
 *     `products` geprobt, 24 ueberleben.
 *
 *  2. GRUNDGESAMTHEIT — der schwerere der beiden Gruende, und durch keinen
 *     Index heilbar. Derselbe Feed hat 15.074 nicht-optimierte Produkte, aber
 *     nur 24 davon haben eine `pre_optimization`-Score-Zeile. Produkte ohne
 *     Score des aktiven Typs sind fuer eine Score-Abfrage unsichtbar. Der Knopf
 *     haette 15.074 versprochen und hoechstens 24 geliefert — genau die Klasse
 *     "Beschriftung und Aktion nennen verschiedene Mengen", gegen die dieser
 *     ganze Umbau laeuft.
 *
 * Stattdessen Server-Selection mit `optimizationStatus: 'not_optimized'`:
 * `fetchProductsWithFilters` in `enqueue-optimization-batches` paginiert
 * `products` per Cursor ueber den PK-Index (kein Join, kein Sortierschritt),
 * und die Auswahl kommt damit aus DERSELBEN Quelle wie die Zahl auf dem Knopf
 * (`products.optimization_status` via `useProductStats`). Die
 * Kontingent-Kappung passiert dort serverseitig gleich mit.
 *
 * PREIS, BEWUSST GEZAHLT: "schwaechste zuerst" entfaellt — der Server trimmt in
 * `id`-Reihenfolge. Das weicht von Spec-Abschnitt 8 ab. Die Sortier-Zusage war
 * allerdings nie eingeloest: auf dem Feed oben konnte sie 24 von 15.074
 * Produkten sortieren und die restlichen 15.050 gar nicht erst anbieten. Sie
 * sauber wiederherzustellen hiesse, serverseitig in
 * `enqueue-optimization-batches` zu sortieren — eigener Vorgang, eigener Plan.
 *
 * Den Join "als Optimierung" zurueckzubauen bringt beide Befunde zurueck.
 * ────────────────────────────────────────────────────────────────────────────
 */
export function useFeedCheckOptimize(
  feedId: string | null,
  onOptimize: (opts: OptimizeOpts) => void | Promise<void>,
  target: number,
): UseFeedCheckOptimizeResult {
  const [loading, setLoading] = useState(false);
  // `loading` state alone can't guard same-tick double clicks — setState
  // doesn't apply synchronously, so two calls before the first re-render
  // both read the same stale `false`. A ref mutates immediately.
  const inFlightRef = useRef(false);

  const runOptimize = async (source: FeedCheckOptimizeSource) => {
    // `target <= 0` heisst: es gibt nichts anzubieten (Kontingent leer oder
    // Bestand abgearbeitet). Die Seite blendet den Knopf dann gar nicht erst
    // ein; der Riegel hier ist Defense-in-Depth und feuert bewusst VOR dem
    // Analytics-Ereignis, damit ein unmoeglicher Klick die Kennzahl nicht
    // verwaessert.
    if (!feedId || target <= 0 || inFlightRef.current) return;
    inFlightRef.current = true;
    trackConversion('feed_check_upsell_click', { source });
    setLoading(true);
    try {
      await onOptimize({
        optimizationType: 'both,attributes',
        isAdvanced: true,
        // Server-Selection: der Server waehlt selbst aus `products`. Vorlage
        // ist `onOptimizeAll` des Onboarding-Assistenten, derselbe erprobte
        // Pfad. `estimatedCount` speist die Kontingent-Vorwarnung in
        // `handleOptimize`.
        selectAllMode: { enabled: true, estimatedCount: target },
        advancedSelectedIds: new Set<string>(),
        advancedFilters: { optimizationStatus: 'not_optimized' },
        hasUnoptimizedProducts: true,
        totalProductCount: target,
        // Dieser Lauf ist per Konstruktion rein neu: gefiltert wird auf
        // `not_optimized`, und `target` ist bereits gegen die freie Kapazitaet
        // gedeckelt. Damit ist der Feed-Check der einzige Einstieg, der dem
        // Kapazitaets-Gate und der Vorwarnung in `handleOptimize` eine belegte
        // Zahl geben kann statt einer Schaetzung.
        newProductCount: target,
      });
    } catch (err) {
      // Kein eigener Fehler-Zustand mehr: `handleOptimize` faengt jeden
      // Fehlerpfad selbst ab (Toast bzw. Dialog) und wirft nicht. Dieser
      // Riegel verhindert nur eine unbehandelte Rejection, falls das eines
      // Tages nicht mehr stimmt — eine Meldung, die der Nutzer nie sieht,
      // waere schlimmer als keine.
      Sentry.captureException(err, { tags: { feature: 'feed-check', action: 'upsell-run' } });
    } finally {
      setLoading(false);
      inFlightRef.current = false;
    }
  };

  return { runOptimize, loading };
}
